import { CelebrationOverlay } from "@/components/Celebration/CelebrationOverlay.tsx";
import { api } from "@/lib/http.ts";
import type { MarkMessageActionFiredReq } from "@/types/messages.ts";
import { type ReactNode, useCallback, useEffect, useRef } from "react";
import { useMarkdownContext } from "./context.tsx";

/**
 * A moment worth marking: a page-level glow-and-confetti burst, fired when the
 * message carrying it renders.
 *
 * Written as `[Welcome aboard, Kiran! 🎉](action:celebrate)`. The link text is
 * the celebration the reader sees, so every other surface — iOS, email, SMS —
 * renders that text as ordinary prose and simply skips the confetti. Nothing has
 * to know what `celebrate` means to display the message correctly.
 *
 * What is left here is the gating: whether this message has already been
 * celebrated, and recording that it has. The choreography itself is
 * {@link CelebrationOverlay}, which the tasks list plays too — mounting it is
 * the celebration starting, so this renders it only once it is owed.
 *
 * It plays once per message, and the record of that lives on the server rather
 * than in this browser: someone who finishes onboarding on their phone and then
 * opens the web app should not be congratulated a second time.
 */
export const Celebration = ({ children }: { children: ReactNode }) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const { message } = useMarkdownContext();
  const alreadyFired = message?.clientState.firedActions?.includes("celebrate") ?? false;

  // A long message can run past a screen, and the active-message glide anchors
  // the user's echo at the top — leaving the line worth celebrating below the
  // fold, confetti firing over something they cannot see. Bring it into view
  // once the message has settled; `nearest` makes this a no-op wherever it is
  // already visible (a reopened thread rests at the bottom). Smooth only when
  // motion is allowed; the reduced-motion reader still gets the visibility.
  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const t = setTimeout(() => {
      textRef.current?.scrollIntoView({
        behavior: reduced ? "auto" : "smooth",
        block: "nearest",
      });
    }, 400);
    return () => clearTimeout(t);
  }, []);

  /**
   * Recorded once the choreography is visibly underway, never at mount: an
   * earlier guard marked itself at effect start and hid a bug by recording a
   * celebration that painted nothing.
   */
  const markFired = useCallback(() => {
    if (!message) return;
    const body: MarkMessageActionFiredReq = { messageId: message.id, action: "celebrate" };
    void api.post("/api/a/messages/action-fired", body);
  }, [message]);

  return (
    <span ref={textRef}>
      {children}
      {/* Nothing to record against means nothing to stop a replay on the next
          render, so the celebration stays put rather than repeating forever. */}
      {message && !alreadyFired && <CelebrationOverlay onStarted={markFired} />}
    </span>
  );
};
